"use client";

import ProductPage from "@/components/ProductPage";
import ImageHero from "./hero.jpg";
import ImageBeratung from "./beratung.jpg";
import ImageUI from "./UI.jpg";
import ImageCross from "./cross.jpg";
import ImageApii from "./apii.jpg";
import ImageTesting from "./testing.jpg";
import ImageDevice from "./device.jpg";
import { Check } from "lucide-react";
import Section from "@/components/Section";
import ImageText from "@/components/ImageText";

export default function AppEntwicklung() {
  return (
    <ProductPage
      productName="App-Entwicklung"
      hero={{
        title: "Maßgeschneiderte App-Entwicklung",
        subtitle:
          "Mobile Anwendungen für iOS, Android und plattformübergreifend – von der Idee bis zur Veröffentlichung.",
        buttonLeft: {
          title: "Unverbindliche Anfrage",
          href: "/support",
        },
        buttonRight: { title: "Kontakt aufnehmen", href: "/kontakt" },
        image: ImageHero,
        imageAlt: "App-Entwicklung",
      }}
      highlight={{
        image: ImageHero,
        bullets: [
          "Wir entwickeln native und plattformübergreifende Lösungen (z. B. iOS und Android) mit Fokus auf Performance, Sicherheit und Zukunftsfähigkeit – individuell auf Ihr Projekt zugeschnitten.",
          "Durch enge Zusammenarbeit, iterative Entwicklung und transparente Kommunikation entsteht Software, die Nutzer begeistert und Ihr Business stärkt.",
          "Von der ersten Idee über Design und Entwicklung bis hin zur Veröffentlichung im App Store und Play Store begleiten wir Sie durch den gesamten Prozess.",
        ],
      }}
      beforeVideo={
        <Section className="bg-background text-foreground py-12 md:py-20">
          <ImageText
            imagePosition="right"
            imageSlot={
              <div className="relative overflow-hidden mt-15 rounded-2xl border border-black/10 bg-black shadow-sm h-100">
                <video className="h-full w-full object-cover" controls>
                  <source src="/VideoFürWebseite.mp4" type="video/mp4" />
                </video>
              </div>
            }
            title="Maßgeschneiderte Apps – von der Idee bis zum Launch"
          >
            <p className="text-slate-700 leading-relaxed">
              Mit vielen Jahren Erfahrung in der mobilen Entwicklung realisieren
              wir individuelle Apps, die durch Stabilität, hohe Usability und
              modernen Technologieeinsatz überzeugen – von der ersten Idee bis
              zur erfolgreichen Veröffentlichung.
            </p>
            <p className="mt-4 text-slate-700 leading-relaxed">
              Ob native iOS- und Android-App oder plattformübergreifende Lösung
              – wir wählen die passende Technologie und begleiten Sie durch
              jeden Schritt: Konzept, Design, Entwicklung, Testing und
              Store-Einreichung.
            </p>

            <ul className="mt-4 space-y-2">
              <li className="flex items-start gap-3 text-slate-700">
                <Check className="shrink-0 mt-1 text-brand-primary" size={18} />
                <span>
                  <strong>Ganzheitliche Betreuung:</strong> Von der
                  Anforderungsanalyse bis zur Veröffentlichung.
                </span>
              </li>
              <li className="flex items-start gap-3 text-slate-700">
                <Check className="shrink-0 mt-1 text-brand-primary" size={18} />
                <span>
                  <strong>Flexibel & Skalierbar:</strong> Native oder
                  Cross-Platform – passend zu Ihrem Projekt.
                </span>
              </li>
              <li className="flex items-start gap-3 text-slate-700">
                <Check className="shrink-0 mt-1 text-brand-primary" size={18} />
                <span>
                  <strong>Langfristige Partnerschaft:</strong> Wartung, Updates
                  und Weiterentwicklung inklusive.
                </span>
              </li>
            </ul>
          </ImageText>
        </Section>
      }
      features={[
        {
          src: ImageBeratung,
          alt: "Beratung und Anforderungsanalyse für App-Projekte",
          title: "Beratung & Anforderungsanalyse",
          desc: "Wir hören zu, verstehen Ihre Ziele und entwickeln gemeinsam ein klares Konzept – technisch fundiert und nutzerorientiert.",
        },
        {
          src: ImageUI,
          alt: "UX/UI Design und Prototyping für mobile Apps",
          title: "UX/UI Design & Prototyping",
          desc: "Ein ansprechendes Design und eine intuitive Nutzererfahrung stehen im Fokus jeder App, die wir entwickeln.",
        },
        {
          src: ImageCross,
          alt: "Native und Cross-Platform App Entwicklung für iOS und Android",
          title: "Native & Cross-Platform Entwicklung",
          desc: "Ob iOS, Android oder plattformübergreifend – wir wählen die passende Technologie für Ihre Anforderungen.",
        },
        {
          src: ImageApii,
          alt: "Integration mit Backend-Systemen und APIs",
          title: "Integration & Schnittstellen",
          desc: "Wir verbinden Ihre App sicher und effizient mit bestehenden Backend-Systemen, APIs oder Cloud-Services.",
        },
        {
          src: ImageTesting,
          alt: "Testing, Qualitätssicherung und App-Performance-Optimierung",
          title: "Testing & Qualitätssicherung",
          desc: "Durch umfangreiche Tests auf unterschiedlichen Geräten garantieren wir hohe Stabilität und beste Performance.",
        },
        {
          src: ImageDevice,
          alt: "Veröffentlichung in App Stores und laufende Wartung",
          title: "Veröffentlichung & Wartung",
          desc: "Wir unterstützen bei der Store-Einreichung (Apple & Google), kümmern uns um Updates und langfristige Betreuung.",
        },
      ]}
      faqs={[
        {
          q: "Welche Arten von Apps entwickeln Sie?",
          a: "Wir entwickeln native iOS- und Android-Apps sowie Cross-Platform-Lösungen. Ob Kunden-App, POS-System, interne Prozess-App oder Unternehmenslösung – wir passen die Technologie an Ihr Ziel an.",
        },
        {
          q: "Wie läuft die App-Entwicklung bei Ihnen ab?",
          a: "Von der Beratung über das Design bis zur Veröffentlichung begleiten wir Sie agil durch alle Phasen: Konzept, Prototyp, Entwicklung, Test, Store-Einreichung und laufende Wartung.",
        },
        {
          q: "Welche Technologien verwenden Sie?",
          a: "Wir nutzen moderne Frameworks wie Swift, Kotlin, Flutter und React Native sowie skalierbare Backend-Systeme. So stellen wir Leistung, Sicherheit und Zukunftsfähigkeit sicher.",
        },
        {
          q: "Können bestehende Systeme eingebunden werden?",
          a: "Ja. Wir integrieren Ihre bestehenden Systeme und Datenbanken über sichere API-Schnittstellen, um eine reibungslose Verbindung zwischen App und Backend zu gewährleisten.",
        },
        {
          q: "Unterstützen Sie auch nach der Veröffentlichung?",
          a: "Selbstverständlich. Wir bieten langfristige Betreuung, regelmäßige Updates, Monitoring und Weiterentwicklung – damit Ihre App stets aktuell und performant bleibt.",
        },
        {
          q: "Was kostet die Entwicklung einer App?",
          a: "Die Kosten hängen von Funktionsumfang, Design, Plattform und Integrationen ab. Nach einem kostenlosen Erstgespräch erhalten Sie ein transparentes Angebot.",
        },
        {
          q: "Wie lange dauert ein App-Projekt?",
          a: "Je nach Umfang dauert ein typisches App-Projekt zwischen 8 und 20 Wochen – von der Konzeption bis zur Veröffentlichung.",
        },
        {
          q: "Bieten Sie auch Unterstützung bei der Store-Einreichung?",
          a: "Ja. Wir begleiten Sie durch den gesamten Veröffentlichungsprozess in den App Stores und kümmern uns um technische Anforderungen, Richtlinien und Updates.",
        },
        {
          q: "Können Sie auch interne Unternehmens-Apps entwickeln?",
          a: "Ja. Wir realisieren maßgeschneiderte Lösungen für interne Prozesse wie Reporting, Mitarbeitermanagement oder Logistik – sicher und effizient.",
        },
        {
          q: "Was sind die typischen Einsatzbereiche Ihrer Apps?",
          a: "Unsere Apps unterstützen Unternehmen bei Vertrieb, Kundenbindung, Prozessoptimierung und Digitalisierung – ob im Handel, Service, Außendienst oder Management.",
        },
      ]}
    />
  );
}
